import { set, get, del, keys } from 'idb-keyval';

// ⚡️ PERFORMANCE OPTIMIZATION: Advanced offline storage with IndexedDB
class OfflineStorage {
  constructor() {
    this.CACHE_PREFIX = 'chat_cache_';
    this.CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours
    this.MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Cache messages with metadata
  async cacheMessages(conversationId, messages) {
    try {
      const cacheKey = `${this.CACHE_PREFIX}${conversationId}`;
      const cacheData = {
        messages,
        timestamp: Date.now(),
        version: '1.0',
        messageCount: messages.length
      };
      
      await set(cacheKey, cacheData);
      console.log(`Cached ${messages.length} messages for conversation ${conversationId}`);
      
      // ⚡️ PERFORMANCE OPTIMIZATION: Cleanup old cache entries
      await this.cleanupOldCache();
    } catch (error) {
      console.error('Failed to cache messages:', error);
    }
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Get cached messages with validation
  async getCachedMessages(conversationId) {
    try {
      const cacheKey = `${this.CACHE_PREFIX}${conversationId}`;
      const cacheData = await get(cacheKey);
      
      if (!cacheData) return null;
      
      // ⚡️ PERFORMANCE OPTIMIZATION: Check cache expiry
      const isExpired = Date.now() - cacheData.timestamp > this.CACHE_EXPIRY;
      if (isExpired) {
        await this.clearCache(conversationId);
        return null;
      }
      
      console.log(`Loaded ${cacheData.messageCount} messages from cache for conversation ${conversationId}`);
      return cacheData.messages;
    } catch (error) {
      console.error('Failed to get cached messages:', error);
      return null;
    }
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Clear specific conversation cache
  async clearCache(conversationId) {
    try {
      const cacheKey = `${this.CACHE_PREFIX}${conversationId}`;
      await del(cacheKey);
      console.log(`Cleared cache for conversation ${conversationId}`);
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Cleanup old cache entries
  async cleanupOldCache() {
    try {
      const allKeys = await keys();
      const cacheKeys = allKeys.filter(key => key.startsWith(this.CACHE_PREFIX));
      
      let totalSize = 0;
      const cacheEntries = [];
      
      // Calculate total cache size
      for (const key of cacheKeys) {
        const data = await get(key);
        if (data) {
          const size = JSON.stringify(data).length;
          totalSize += size;
          cacheEntries.push({ key, data, size, timestamp: data.timestamp });
        }
      }
      
      // ⚡️ PERFORMANCE OPTIMIZATION: Remove old entries if cache is too large
      if (totalSize > this.MAX_CACHE_SIZE) {
        // Sort by timestamp (oldest first)
        cacheEntries.sort((a, b) => a.timestamp - b.timestamp);
        
        // Remove oldest entries until we're under the limit
        for (const entry of cacheEntries) {
          await del(entry.key);
          totalSize -= entry.size;
          
          if (totalSize <= this.MAX_CACHE_SIZE * 0.8) { // Keep 20% buffer
            break;
          }
        }
        
        console.log('Cleaned up old cache entries');
      }
    } catch (error) {
      console.error('Failed to cleanup cache:', error);
    }
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Cache file uploads for offline retry
  async cacheFailedUpload(file, conversationId) {
    try {
      const uploadKey = `upload_${conversationId}_${Date.now()}`;
      const uploadData = {
        file: await this.fileToBase64(file),
        fileName: file.name,
        fileType: file.type,
        conversationId,
        timestamp: Date.now()
      };
      
      await set(uploadKey, uploadData);
      console.log(`Cached failed upload: ${file.name}`);
    } catch (error) {
      console.error('Failed to cache upload:', error);
    }
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Get failed uploads for retry
  async getFailedUploads(conversationId) {
    try {
      const allKeys = await keys();
      const uploadKeys = allKeys.filter(key => 
        key.startsWith('upload_') && key.includes(conversationId)
      );
      
      const failedUploads = [];
      for (const key of uploadKeys) {
        const uploadData = await get(key);
        if (uploadData) {
          failedUploads.push(uploadData);
        }
      }
      
      return failedUploads;
    } catch (error) {
      console.error('Failed to get failed uploads:', error);
      return [];
    }
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Convert file to base64 for storage
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Convert base64 back to file
  base64ToFile(base64, fileName, fileType) {
    const byteString = atob(base64.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    
    return new File([ab], fileName, { type: fileType });
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Get cache statistics
  async getCacheStats() {
    try {
      const allKeys = await keys();
      const cacheKeys = allKeys.filter(key => key.startsWith(this.CACHE_PREFIX));
      
      let totalMessages = 0;
      let totalSize = 0;
      
      for (const key of cacheKeys) {
        const data = await get(key);
        if (data) {
          totalMessages += data.messageCount || 0;
          totalSize += JSON.stringify(data).length;
        }
      }
      
      return {
        conversationCount: cacheKeys.length,
        totalMessages,
        totalSize: `${(totalSize / 1024 / 1024).toFixed(2)}MB`
      };
    } catch (error) {
      console.error('Failed to get cache stats:', error);
      return null;
    }
  }
}

// ⚡️ PERFORMANCE OPTIMIZATION: Export singleton instance
export const offlineStorage = new OfflineStorage();
export default offlineStorage; 